import * as THREE from 'three';
import { LODStaticObject, StaticObjectType } from './LODStaticObject';
import { BoundingBox } from '../../physics/BoundingVolume';
import { LODLevel } from '../../optimization/LODSystem';
import { StaticObject } from './StaticObject';

/**
 * Optimized Tree with Level of Detail support
 * Uses the improved LODStaticObject base class for efficient LOD transitions
 * Composed of low-poly shapes with flat shading to show triangles
 */
export class LODTree extends LODStaticObject {
  // Track tree dimensions for appropriate bounding volume
  private treeHeight: number;
  private treeWidth: number;
  
  constructor() {
    // Create trunk material with flat shading - dark brown
    const treeTrunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x5D4037, // Dark brown
      roughness: 0.9,
      metalness: 0.1,
      flatShading: true, // Enable flat shading to see triangles
      shadowSide: THREE.FrontSide // Properly cast shadows
    });
    
    // Create leaves material with flat shading - dark green
    const treeLeavesMaterial = new THREE.MeshStandardMaterial({
      color: 0x2E7D32, // Dark green
      roughness: 0.8,
      metalness: 0.1,
      flatShading: true, // Enable flat shading to see triangles
      shadowSide: THREE.FrontSide
    });
    
    // Create lighter leaves material for lower LOD levels
    const treeLeavesLightMaterial = new THREE.MeshStandardMaterial({
      color: 0x388E3C, // Medium green for low LOD
      roughness: 0.8,
      metalness: 0.1,
      flatShading: true,
      shadowSide: THREE.FrontSide
    });
    
    // Set tree dimensions - this creates taller trees without varying height too much
    // to avoid accessing 'this' before super() call
    const treeHeight = 14; // Fixed height for all trees
    const treeWidth = 2.5; // Fixed width for all trees
    
    // Create high-detail multi-material (trunk and leaves)
    const highDetailMaterials = [treeTrunkMaterial, treeLeavesMaterial];
    const highDetailMultiMaterial = highDetailMaterials;
    
    // Create medium-detail multi-material
    const mediumDetailMaterials = [treeTrunkMaterial, treeLeavesMaterial];
    const mediumDetailMultiMaterial = mediumDetailMaterials;
    
    // Create low-detail multi-material
    const lowDetailMaterials = [treeTrunkMaterial, treeLeavesLightMaterial];
    const lowDetailMultiMaterial = lowDetailMaterials;
    
    // Create with tree-specific configuration
    super({
      type: StaticObjectType.TREE,
      name: 'LODTree',
      baseScale: new THREE.Vector3(treeWidth, treeHeight, treeWidth),
      materialOverrides: {
        // Use the multi-materials for each LOD level
        high: new THREE.MeshStandardMaterial(), // Placeholder - will be replaced with material array
        medium: new THREE.MeshStandardMaterial(), // Placeholder - will be replaced with material array
        low: new THREE.MeshStandardMaterial() // Placeholder - will be replaced with material array
      }
    });
    
    // Manually replace the materials with the multi-materials after super() call
    this.replaceMaterials(LODLevel.HIGH, highDetailMultiMaterial);
    this.replaceMaterials(LODLevel.MEDIUM, mediumDetailMultiMaterial);
    this.replaceMaterials(LODLevel.LOW, lowDetailMultiMaterial);
    
    // Store dimensions after super() call
    this.treeHeight = treeHeight;
    this.treeWidth = treeWidth;
    
    // Add a bounding box for collision
    this.createBoundingVolume();
  }
  
  /**
   * Create bounding volume for collision detection
   */
  private createBoundingVolume(): void {
    // Create a box that's narrower than the visual size for better gameplay
    // Trees are narrow at the bottom, so we make the collision box narrower
    const halfExtents = new THREE.Vector3(
      0.6,     // Narrow trunk - slightly wider than before
      this.treeHeight / 2.0,  // Half height
      0.6      // Narrow trunk - slightly wider than before
    );
    
    const boundingBox = new BoundingBox(halfExtents, this.getPosition());
    this.setBoundingVolume(boundingBox);
  }
  
  /**
   * Replace the materials with multi-materials to support different trunk and leaf materials
   */
  private replaceMaterials(level: LODLevel, materials: THREE.Material[]): void {
    // Get instance mesh names
    const meshName = this.getTreeInstanceName(level);
    const mesh = StaticObject.instancedMeshes.get(meshName);
    
    if (mesh && mesh instanceof THREE.InstancedMesh) {
      // Replace the material with the multi-material
      mesh.material = materials;
    }
  }
  
  /**
   * Get the instance name for a given LOD level specifically for trees
   */
  private getTreeInstanceName(level: LODLevel): string {
    switch (level) {
      case LODLevel.HIGH:
        return 'lodTreeHigh';
      case LODLevel.MEDIUM:
        return 'lodTreeMedium';
      case LODLevel.LOW:
        return 'lodTreeLow';
      default:
        return 'lodTreeLow';
    }
  }
} 